import { useMutation, useQueryClient, type UseMutationOptions, type UseMutationResult } from 'react-query';
import { handleFactoryError } from './globalErrorHandler';

export interface EntityMutationConfig<TData, TVariables> {
  mutationFn: (variables: TVariables) => Promise<TData>;
  invalidateKeys?: string[];
  /** If true, suppress the global error toast for this mutation. */
  silent?: boolean;
  options?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>;
}

/**
 * Factory that creates a typed useMutation wrapper.
 * Automatically invalidates the specified query keys on success.
 * Automatically shows error toasts via the registered global error handler
 * unless `silent: true` is set in the config or `onError` is provided in overrides.
 *
 * Returns a hook function: (overrides?) => UseMutationResult
 */
export function createEntityMutation<TData, TVariables>(
  config: EntityMutationConfig<TData, TVariables>
) {
  return function useEntityMutation(
    overrides?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>
  ): UseMutationResult<TData, unknown, TVariables> {
    const queryClient = useQueryClient();

    return useMutation(config.mutationFn, {
      ...config.options,
      ...overrides,
      onSuccess: (data, variables, context) => {
        if (config.invalidateKeys) {
          config.invalidateKeys.forEach((key) => {
            void queryClient.invalidateQueries(key);
          });
        }
        config.options?.onSuccess?.(data, variables, context);
        overrides?.onSuccess?.(data, variables, context);
      },
      onError: (error: unknown, variables, context) => {
        if (!config.silent && !overrides?.onError) {
          handleFactoryError(error);
        }
        config.options?.onError?.(error, variables, context);
        overrides?.onError?.(error, variables, context);
      },
    });
  };
}
